import { Fragment, memo, useCallback, useEffect, useRef, useState } from "react";
import { ApolloError } from "@apollo/client";
import { Input, Modal, Select, message } from "antd";
import {
	FileText,
	ExternalLink,
	BookOpen,
	MapPin,
	CheckCircle2,
	AlertTriangle,
	XCircle,
	Info,
	AlertCircle,
	ShieldCheck,
	Scale,
	Link2,
	Pencil,
	RotateCcw,
	MessageSquare,
	Wrench
} from "lucide-react";

import ModalNavControls from "components/modal-nav-controls/modal-nav-controls";
import CodeSectionPanel, {
	CodeSectionReference,
	CrossReferencedCodeSection,
	crossReferenceToCodeSectionReference
} from "components/code-section-panel/code-section-panel";
import CodeCitationPicker from "components/code-citation-picker/code-citation-picker";
import PlanReviewFeedbackSection, {
	PlanReviewFeedbackDecisionRequest,
	PlanReviewFeedbackRow,
	PlanReviewFeedbackSubmission
} from "components/plan-review-feedback-section/plan-review-feedback-section";
import PlanReviewFindingHistory from "components/plan-review-finding-history/plan-review-finding-history";
import PlanReviewOllieOriginal from "components/plan-review-ollie-original/plan-review-ollie-original";
import PlanReviewApplicantFeedbackCard from "components/plan-review-applicant-feedback-card/plan-review-applicant-feedback-card";
import {
	canSeeReviewerTools,
	isAuthorityRole,
	isBlockingPlanReviewFindingStatus,
	PlanReviewFeedbackDecision,
	PlanReviewFindingStatus
} from "constants/field-values";
import { PLAN_REVIEW_FINDING_STATUS_OPTIONS, PLAN_REVIEW_SEVERITY_OPTIONS } from "constants/constants";
import { useAddPlanReviewFeedbackMutation } from "graphql/mutations/add-plan-review-feedback.generated";
import {
	UpdatePlanReviewFeedbackMutation,
	useUpdatePlanReviewFeedbackMutation
} from "graphql/mutations/update-plan-review-feedback.generated";
import {
	DeletePlanReviewFeedbackMutation,
	useDeletePlanReviewFeedbackMutation
} from "graphql/mutations/delete-plan-review-feedback.generated";
import usePlanReviewFindingOverride from "hooks/usePlanReviewFindingOverride";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { toExternalUrl } from "utils/strings";
import {
	PlanReviewFindingChangeRow,
	buildFindingHistory,
	changedFindingFields,
	findingSeverityLabel,
	findingStatusLabel,
	formatPersonWithOrganization,
	formatReviewDateTime,
	isFindingEditedByReviewer,
	isFindingRevertedToOllie,
	latestApplicantFeedback,
	latestReviewerChange,
	ollieValuesOf
} from "utils/plan-review-finding-review";

// The report reads the findings, and the feedback nested in them, from this query. Every
// feedback mutation refetches it so the modal shows the saved row without a page reload.
const FEEDBACK_REFETCH_QUERIES = ["getPlanReviewFindings"];

// Header text of the feedback panel. Authority roles see the reviewer version; every other
// role sees the applicant version.
const REVIEWER_FEEDBACK_COPY = {
	title: "Reviewer Feedback",
	subtitle: "Rate Ollie’s analysis accuracy to help improve future plan reviews."
};
const APPLICANT_FEEDBACK_COPY = {
	title: "Applicant Feedback",
	subtitle:
		"Rate Ollie’s analysis accuracy to help improve future plan reviews. Your feedback will be shared with the reviewer after you submit to the AHJ."
};

// Shown in the applicant's feedback form when Incorrect is selected on a finding that blocks the
// approval, because saving that rating moves the finding to the "Dismissed" group.
const APPLICANT_DISMISS_NOTICE = (
	<>
		This finding will move to the <strong>Dismissed</strong> section at the bottom of the findings list and your note
		will be visible to the reviewer.
	</>
);

// Stacked above the modal overlay (z-index 1000), so the confirm dialog is not hidden behind it.
const REVERT_CONFIRM_Z_INDEX = 1300;

// The finding shape the report already has from the job subscription, narrowed to what
// the modal renders. Declared structurally so the caller can pass its row straight in.
export interface PlanReviewFindingModalItem {
	id: string;
	job_id: string;
	status?: string | null;
	severity?: string | null;
	confidence?: number | null;
	category?: string | null;
	/** The category as the jurisdiction names it today; `category` is what the report recorded. */
	category_record?: { name: string } | null;
	title?: string | null;
	summary?: string | null;
	corrective_action?: string | null;
	/** Values that Ollie wrote. The fields above are the current ones, which the reviewer can change. */
	ollie_status?: string | null;
	ollie_severity?: string | null;
	ollie_summary?: string | null;
	ollie_corrective_action?: string | null;
	/** Code sections set by the reviewer. Null when the finding uses its requirement's sections. */
	code_sections?: unknown;
	created_at?: string | null;
	sheet_reference?: string | null;
	page_number?: number | null;
	/** Superseded by sheet_reference + page_number; still set on findings analyzed before the split. */
	page_reference?: string | null;
	rule_id?: string | null;
	requirement?: {
		id: string;
		code?: string | null;
		title?: string | null;
		category_record?: { name: string } | null;
		requirement_type?: string | null;
		source?: string | null;
		section?: string | null;
		source_url?: string | null;
		/** The adopted code the rule rests on. The analysis judged the package against
		 *  this text as well as the rule, so the reviewer is shown the same thing. */
		plan_review_requirement_code_sections?: CodeSectionReference[];
		/** The sections that those code sections refer to. The analysis read them too. */
		cross_referenced_sections?: CrossReferencedCodeSection[];
	} | null;
	document?: {
		id: string;
		file_name?: string | null;
	} | null;
	plan_review_citations?: {
		id: string;
		tier?: string | null;
		source?: string | null;
		section?: string | null;
		source_url?: string | null;
		page_numbers?: unknown;
	}[];
	plan_review_feedbacks?: (PlanReviewFeedbackRow & { user_name?: string | null; organization_name?: string | null })[];
	plan_review_finding_changes?: PlanReviewFindingChangeRow[];
}

interface PlanReviewFindingModalProps {
	isOpen: boolean;
	onClose: () => void;
	item: PlanReviewFindingModalItem | null;
	/** Only the latest submittal can be changed or rated. Older submittals are read-only. */
	isLatestSubmittal: boolean;
	/** Number of the submittal, for the read-only badge. */
	submissionNumber?: number | null;
	/** Jurisdiction of the plan review. The reviewer picks code sections from the code it adopted. */
	jurisdictionId?: string | null;
	/** Step to the previous/next report item without leaving the modal. Both are optional:
	 *  when the caller has no list to walk, only the close button renders. */
	onPrevious?: () => void;
	onNext?: () => void;
	hasPrevious?: boolean;
	hasNext?: boolean;
	/** The submitted document this finding points at, when it has one. */
	document?: {
		id: string;
		file_name?: string | null;
		file_url?: string | null;
		mime_type?: string | null;
		file_size_bytes?: number | null;
		doc_type?: string | null;
	} | null;
}

const STATUS_META: Record<string, { label: string; className: string; icon: typeof CheckCircle2 }> = {
	[PlanReviewFindingStatus.PASS]: { label: "Pass", className: "prfm-status-pass", icon: CheckCircle2 },
	[PlanReviewFindingStatus.WARNING]: { label: "Warning", className: "prfm-status-warning", icon: AlertTriangle },
	[PlanReviewFindingStatus.FAIL]: { label: "Fail", className: "prfm-status-fail", icon: XCircle },
	[PlanReviewFindingStatus.MISSING]: { label: "Missing", className: "prfm-status-missing", icon: AlertCircle },
	[PlanReviewFindingStatus.NOT_APPLICABLE]: { label: "Not Applicable", className: "prfm-status-skipped", icon: Info }
};

const DOC_TYPE_LABELS: Record<string, string> = {
	plans: "Plans",
	calculations: "Calculations",
	cut_sheets: "Cut Sheets",
	application_form: "Application Form",
	cover_letter: "Cover Letter",
	other: "Document"
};

const formatBytes = (bytes?: number | null): string => {
	if (!bytes) return "";
	const units = ["B", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(1024));
	return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

// page_numbers is a jsonb column, so it arrives as a parsed array or as a string.
// Anything that is not a usable page is dropped: the numbers become links, and a link
// to page 0 or to "12-14" would just open the file at the wrong place.
const parsePageNumbers = (raw: unknown): number[] => {
	if (!raw) return [];
	const list = Array.isArray(raw)
		? raw
		: (() => {
				try {
					const parsed = JSON.parse(String(raw));
					return Array.isArray(parsed) ? parsed : [];
				} catch {
					return [];
				}
			})();
	const pages = list.map(Number).filter(page => Number.isInteger(page) && page > 0);
	return [...new Set(pages)];
};

// The #page anchor is read by the browser's PDF viewer, so it only helps on a file it
// opens as a PDF; on anything else the plain URL is kept.
const isPdf = (url?: string | null, mimeType?: string | null): boolean =>
	mimeType === "application/pdf" || url?.split("?")[0]?.toLowerCase().endsWith(".pdf") === true;

const atPage = (url: string, page?: number | null): string => (page ? `${url}#page=${page}` : url);

// Where the finding is, as the reviewer reads it. The sheet printed on the drawing and
// the page inside the PDF are different numbers — sheet 27 is often page 31 — so both
// are always shown when both are known. Findings analyzed before the two were split
// only have the sentence the model wrote.
const formatLocation = (item: PlanReviewFindingModalItem): string => {
	const sheet = item.sheet_reference?.trim();
	const parts: string[] = [];
	if (sheet) parts.push(`Sheet ${sheet}`);
	if (item.page_number) parts.push(`Page ${item.page_number}`);
	return parts.join(" · ") || (item.page_reference ?? "");
};

const confidenceLabel = (confidence?: number | null): string => {
	if (confidence === null || confidence === undefined) return "—";
	if (confidence >= 0.8) return "High";
	if (confidence >= 0.5) return "Medium";
	return "Low";
};

/**
 * Detail view for one Review Category check, opened from the compliance report.
 *
 * Mirrors the Ollie EvidenceModal: the referenced document at the top, then the
 * reviewer's observation, the compliance verdict and the citation behind it. The
 * document section is hidden when the finding is not tied to one — which is the normal
 * case for a MISSING finding, where the point is that no document was submitted.
 */
const PlanReviewFindingModal = ({
	isOpen,
	onClose,
	item,
	isLatestSubmittal,
	submissionNumber,
	jurisdictionId,
	document: submittedDocument,
	onPrevious,
	onNext,
	hasPrevious,
	hasNext
}: PlanReviewFindingModalProps) => {
	const { loggedUserValue } = useLoggedUser();
	const currentUserId = loggedUserValue?.id;
	const bodyRef = useRef<HTMLDivElement>(null);

	// Opens the feedback form with Incorrect selected, from the applicant's "Dismiss" button.
	const [feedbackDecisionRequest, setFeedbackDecisionRequest] = useState<PlanReviewFeedbackDecisionRequest | null>(
		null
	);

	const handleOnCompleteAddPlanReviewFeedbackMutation = useCallback(() => {
		message.success("Thanks — your feedback was recorded.");
	}, []);

	// A row outside the user's permissions (another user's feedback) comes back as null instead
	// of an error, which would otherwise read as a save that silently did nothing.
	const handleOnCompleteUpdatePlanReviewFeedbackMutation = useCallback((data: UpdatePlanReviewFeedbackMutation) => {
		if (!data.update_plan_review_feedback_by_pk) {
			message.error("You can only edit your own feedback.");
			return;
		}
		message.success("Your feedback was updated.");
	}, []);

	const handleOnCompleteDeletePlanReviewFeedbackMutation = useCallback((data: DeletePlanReviewFeedbackMutation) => {
		if (!data.delete_plan_review_feedback_by_pk) {
			message.error("You can only remove your own feedback.");
			return;
		}
		message.success("Your feedback was removed.");
	}, []);

	const handleOnErrorSavePlanReviewFeedbackMutation = useCallback((error: ApolloError) => {
		console.error("Failed to record feedback:", error);
		message.error("Couldn't save your feedback. Please try again.");
	}, []);

	const handleOnErrorDeletePlanReviewFeedbackMutation = useCallback((error: ApolloError) => {
		console.error("Failed to remove feedback:", error);
		message.error("Couldn't remove your feedback. Please try again.");
	}, []);

	// awaitRefetchQueries: the mutation promise resolves only after the findings are
	// refetched, so the panel never shows the empty form before the saved row arrives.
	const [addPlanReviewFeedbackMutation, { loading: adding }] = useAddPlanReviewFeedbackMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorSavePlanReviewFeedbackMutation,
		onCompleted: handleOnCompleteAddPlanReviewFeedbackMutation,
		refetchQueries: FEEDBACK_REFETCH_QUERIES,
		awaitRefetchQueries: true
	});
	const [updatePlanReviewFeedbackMutation, { loading: updating }] = useUpdatePlanReviewFeedbackMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorSavePlanReviewFeedbackMutation,
		onCompleted: handleOnCompleteUpdatePlanReviewFeedbackMutation,
		refetchQueries: FEEDBACK_REFETCH_QUERIES,
		awaitRefetchQueries: true
	});
	const [deletePlanReviewFeedbackMutation, { loading: deleting }] = useDeletePlanReviewFeedbackMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorDeletePlanReviewFeedbackMutation,
		onCompleted: handleOnCompleteDeletePlanReviewFeedbackMutation,
		refetchQueries: FEEDBACK_REFETCH_QUERIES,
		awaitRefetchQueries: true
	});

	// The code sections the finding shows: the reviewer's list when there is one, otherwise the
	// sections that its requirement cites.
	const reviewerCodeSections = Array.isArray(item?.code_sections)
		? (item?.code_sections as CodeSectionReference[])
		: null;
	const codeSections = reviewerCodeSections ?? item?.requirement?.plan_review_requirement_code_sections ?? [];
	const findingOverride = usePlanReviewFindingOverride(item, codeSections);

	// Escape closes, and the page behind must not scroll while the modal is up.
	useEffect(() => {
		if (!isOpen) return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKeyDown);
		const previousOverflow = window.document.body.style.overflow;
		window.document.body.style.overflow = "hidden";
		return () => {
			window.removeEventListener("keydown", onKeyDown);
			window.document.body.style.overflow = previousOverflow;
		};
	}, [isOpen, onClose]);

	// Stepping with Previous/Next keeps the modal mounted, so the body has to be sent back to
	// the top by hand or the next finding opens part-way down. An edit in progress belongs to
	// the finding it started on, so it is closed too.
	useEffect(() => {
		bodyRef.current?.scrollTo({ top: 0 });
		findingOverride.cancelEditing();
		findingOverride.hideFeedbackPrompt();
		setFeedbackDecisionRequest(null);
	}, [item?.id]);

	const categoryLabel = item?.category_record?.name ?? item?.category ?? null;

	// Authority roles give reviewer feedback; every other role gives applicant feedback.
	const isReviewer = isAuthorityRole(loggedUserValue?.role);
	// Feedback rows arrive newest first. The panel shows the latest row written by the user's
	// own group; the reviewer also sees the applicant's latest feedback above the analysis.
	const latestFeedback =
		item?.plan_review_feedbacks?.find(feedback => isAuthorityRole(feedback.user_role) === isReviewer) ?? null;
	// Only the author of a feedback row can edit or delete it. Hasura applies the same rule.
	const isOwnLatestFeedback = !!latestFeedback && latestFeedback.user_id === currentUserId;
	const canSeeReviewerOnlyContent = canSeeReviewerTools(loggedUserValue?.role);
	// Older submittals are read-only: nobody edits or rates their findings any more.
	const canGiveFeedback = !!currentUserId && !!item && isLatestSubmittal;
	const canEditFinding = isReviewer && isLatestSubmittal;
	const isBlockingFinding = isBlockingPlanReviewFindingStatus(item?.status);
	const canDismissFinding = canGiveFeedback && !isReviewer && isBlockingFinding;
	const feedbackCopy = isReviewer ? REVIEWER_FEEDBACK_COPY : APPLICANT_FEEDBACK_COPY;

	// A new rating, for example from the "Dismiss and Leave Feedback" button, writes a new row, so the
	// finding keeps the full history of feedback. "Edit" changes the user's own row in place.
	const handleSubmitFeedback = async ({
		decision,
		errorAttribution,
		reason,
		comment,
		editingFeedbackId
	}: PlanReviewFeedbackSubmission): Promise<boolean> => {
		if (!currentUserId || !item) return false;
		// The messages come from onCompleted / onError. With onError set, Apollo resolves a
		// failed mutation with data undefined instead of rejecting, so data tells the result.
		if (editingFeedbackId) {
			const { data: updatedData } = await updatePlanReviewFeedbackMutation({
				variables: {
					id: editingFeedbackId,
					_set: {
						decision,
						error_attribution: errorAttribution,
						reason,
						comment,
						updated_at: new Date().toISOString()
					}
				}
			});
			return !!updatedData?.update_plan_review_feedback_by_pk;
		}
		const { data } = await addPlanReviewFeedbackMutation({
			variables: {
				input: {
					job_id: item.job_id,
					finding_id: item.id,
					user_id: currentUserId,
					decision,
					error_attribution: errorAttribution,
					reason,
					comment
				}
			}
		});
		return !!data?.insert_plan_review_feedback_one;
	};

	const handleDeleteFeedback = async (): Promise<boolean> => {
		if (!latestFeedback) return false;
		const { data } = await deletePlanReviewFeedbackMutation({ variables: { id: latestFeedback.id } });
		return !!data?.delete_plan_review_feedback_by_pk;
	};

	const handleRevertToOllie = () => {
		Modal.confirm({
			title: "Revert to Ollie’s original finding?",
			content:
				"This restores Status, Severity, Observation, Recommended Action, and Code Sections to Ollie’s original analysis and writes a revert entry to the finding’s history.",
			okText: "Revert finding",
			cancelText: "Cancel",
			zIndex: REVERT_CONFIRM_Z_INDEX,
			onOk: findingOverride.revertToOllie
		});
	};

	if (!isOpen || !item) return null;

	const status = item.status ?? PlanReviewFindingStatus.PASS;
	const meta = STATUS_META[status] ?? STATUS_META[PlanReviewFindingStatus.NOT_APPLICABLE];
	const StatusIcon = meta.icon;
	const crossReferencedSections = (item.requirement?.cross_referenced_sections ?? []).map(
		crossReferenceToCodeSectionReference
	);
	const hasOneCrossReference = crossReferencedSections.length === 1;
	const citation = item.plan_review_citations?.[0] ?? null;
	const citationSource = citation?.source ?? item.requirement?.source ?? null;
	const citationSection = citation?.section ?? item.requirement?.section ?? null;
	const citationUrl = toExternalUrl(citation?.source_url ?? item.requirement?.source_url);
	const citationPages = parsePageNumbers(citation?.page_numbers);

	const location = formatLocation(item);
	const documentIsPdf = isPdf(submittedDocument?.file_url, submittedDocument?.mime_type);
	const documentPdfUrl = documentIsPdf ? (submittedDocument?.file_url ?? null) : null;
	const sourcePdfUrl = citationUrl && isPdf(citationUrl) ? citationUrl : null;

	// The finding's own page is where the reviewer wants to land; a cited page is the
	// fallback for findings that only pinned the citation.
	const documentOpenPage = item.page_number ?? citationPages[0] ?? null;

	// The cited pages are pages of the submitted document, so that is what they open.
	// Only a finding without one — a MISSING check, where nothing was submitted — cites
	// pages of the source itself.
	const pagesTargetUrl = submittedDocument ? documentPdfUrl : sourcePdfUrl;

	// What the reviewer changed, and who did it.
	const { isEditing, draft } = findingOverride;
	const isEdited = isFindingEditedByReviewer(item);
	const changedFields = changedFindingFields(item);
	const ollieValues = ollieValuesOf(item);
	const isReverted = isFindingRevertedToOllie(item);
	const reviewerChange = latestReviewerChange(item);
	const applicantFeedback = latestApplicantFeedback(item);
	const ollieCodeSections = item.requirement?.plan_review_requirement_code_sections ?? [];
	const ollieCodeSectionLabels = ollieCodeSections
		.map(reference => reference.citation_text ?? reference.code_section?.section_number)
		.filter(Boolean)
		.join(", ");
	const hasReviewerCodeSections = reviewerCodeSections !== null;
	const hasNoCodeSections = hasReviewerCodeSections && codeSections.length === 0;
	const correctiveAction = item.corrective_action && item.corrective_action !== "null" ? item.corrective_action : null;
	const history = buildFindingHistory(item, { includeReviewerFeedback: isReviewer });
	const dismissButtonLabel =
		applicantFeedback?.decision === PlanReviewFeedbackDecision.INCORRECT
			? "Update Dismissal"
			: "Dismiss and Leave Feedback";

	return (
		<div className="prfm-overlay" onClick={onClose} role="presentation">
			<div
				className="prfm-modal"
				onClick={event => event.stopPropagation()}
				role="dialog"
				aria-modal="true"
				aria-label={item.title ?? "Finding details"}
			>
				<header className="prfm-header">
					<div className="prfm-header-main">
						<span className={`prfm-status ${meta.className}`}>
							<StatusIcon size={14} />
							{meta.label}
						</span>
						{item.severity && status !== PlanReviewFindingStatus.PASS && (
							<span className="prfm-severity">{item.severity}</span>
						)}
						{categoryLabel && <span className="prfm-category">{categoryLabel}</span>}
						{!isLatestSubmittal && (
							<span className="prfm-read-only">
								Read-only{submissionNumber ? ` · Submittal #${submissionNumber}` : ""}
							</span>
						)}
					</div>
					<ModalNavControls
						onPrevious={onPrevious}
						onNext={onNext}
						hasPrevious={hasPrevious}
						hasNext={hasNext}
						onClose={onClose}
					/>
				</header>

				<div className="prfm-body" ref={bodyRef}>
					<div className="prfm-review-head">
						<div className="prfm-title-row">
							<h2 className="prfm-title">{item.title ?? "Finding"}</h2>
							{canDismissFinding && (
								<button
									type="button"
									className="prfm-highlight-btn"
									onClick={() =>
										setFeedbackDecisionRequest({
											decision: PlanReviewFeedbackDecision.INCORRECT,
											requestId: Date.now()
										})
									}
								>
									<Pencil size={14} />
									{dismissButtonLabel}
								</button>
							)}
							{canEditFinding && (
								<div className="prfm-review-actions">
									{isEditing ? (
										<>
											<button type="button" className="prfm-outline-btn" onClick={findingOverride.cancelEditing}>
												Cancel
											</button>
											<button
												type="button"
												className="prfm-primary-btn prfm-primary-btn-rounded"
												disabled={findingOverride.saving}
												onClick={findingOverride.saveChanges}
											>
												{findingOverride.saving ? "Saving…" : "Save changes"}
											</button>
										</>
									) : (
										<button type="button" className="prfm-highlight-btn" onClick={findingOverride.startEditing}>
											<Pencil size={14} />
											Override Finding
										</button>
									)}
									{isEdited && !isEditing && (
										<button
											type="button"
											className="prfm-outline-btn"
											disabled={findingOverride.reverting}
											onClick={handleRevertToOllie}
										>
											<RotateCcw size={14} />
											Revert to Ollie’s original
										</button>
									)}
								</div>
							)}
						</div>
						{isEdited && reviewerChange && (
							<p className="prfm-updated-by">
								Updated by {formatPersonWithOrganization(reviewerChange.user_name, reviewerChange.organization_name)} ·{" "}
								{formatReviewDateTime(reviewerChange.created_at)}
							</p>
						)}
						{isReverted && <p className="prfm-updated-by">Reverted to Ollie’s original analysis</p>}
						{findingOverride.showFeedbackPrompt && canEditFinding && (
							<div className="prfm-feedback-prompt">
								<span>Override saved. Would you like to leave AI accuracy feedback for Ollie?</span>
								<div className="prfm-feedback-prompt-actions">
									<button
										type="button"
										className="prfm-feedback-prompt-leave"
										onClick={() => {
											findingOverride.hideFeedbackPrompt();
											setFeedbackDecisionRequest({
												decision: PlanReviewFeedbackDecision.PARTIALLY_CORRECT,
												requestId: Date.now()
											});
										}}
									>
										Leave feedback
									</button>
									<button
										type="button"
										className="prfm-feedback-prompt-dismiss"
										onClick={findingOverride.hideFeedbackPrompt}
									>
										Dismiss
									</button>
								</div>
							</div>
						)}
						{applicantFeedback && (
							<PlanReviewApplicantFeedbackCard feedback={applicantFeedback} appearance="highlighted" />
						)}
					</div>

					{submittedDocument && (
						<section className="prfm-section">
							<h3 className="prfm-section-title">
								<FileText size={15} />
								Submitted Document
							</h3>
							<div className="prfm-doc">
								<div className="prfm-doc-icon">
									<FileText size={20} />
								</div>
								<div className="prfm-doc-info">
									<p className="prfm-doc-name">{submittedDocument.file_name}</p>
									<p className="prfm-doc-meta">
										{DOC_TYPE_LABELS[submittedDocument.doc_type ?? "other"] ?? "Document"}
										{submittedDocument.file_size_bytes ? ` · ${formatBytes(submittedDocument.file_size_bytes)}` : ""}
										{location ? ` · ${location}` : ""}
									</p>
								</div>
								{submittedDocument.file_url && (
									<a
										className="prfm-doc-open"
										href={
											documentIsPdf ? atPage(submittedDocument.file_url, documentOpenPage) : submittedDocument.file_url
										}
										target="_blank"
										rel="noreferrer"
									>
										Open
										<ExternalLink size={14} />
									</a>
								)}
							</div>
						</section>
					)}

					<section className="prfm-section">
						<div className="prfm-section-head">
							<h3 className="prfm-section-title">
								<BookOpen size={15} />
								Observation
							</h3>
							{changedFields.summary && (
								<PlanReviewOllieOriginal
									fieldLabel="Observation"
									originalValue={ollieValues.summary}
									className="prfm-original-link"
								/>
							)}
						</div>
						{isEditing && draft ? (
							<Input.TextArea
								className="prfm-textarea"
								rows={3}
								value={draft.summary}
								onChange={event => findingOverride.updateDraft({ summary: event.target.value })}
							/>
						) : (
							<p className="prfm-text">{item.summary ?? "No observation was recorded for this check."}</p>
						)}
						{!submittedDocument && location && (
							<p className="prfm-page-reference">
								<MapPin size={13} />
								{location}
							</p>
						)}
					</section>

					<section className="prfm-section">
						<h3 className="prfm-section-title">
							<ShieldCheck size={15} />
							Compliance
						</h3>
						<div className="prfm-compliance">
							<div className="prfm-compliance-status">
								<div className="prfm-compliance-row">
									<span className="prfm-label">Status</span>
									<div className="prfm-compliance-value">
										{changedFields.status && (
											<PlanReviewOllieOriginal
												fieldLabel="Status"
												originalValue={findingStatusLabel(ollieValues.status)}
												className="prfm-original-link"
											/>
										)}
										{isEditing && draft ? (
											<Select
												className="prfm-select"
												value={draft.status}
												options={PLAN_REVIEW_FINDING_STATUS_OPTIONS.map(option => ({
													value: option.value,
													label: option.label
												}))}
												onChange={(value: string) => findingOverride.updateDraft({ status: value })}
											/>
										) : (
											<span className={`prfm-status ${meta.className}`}>
												<StatusIcon size={14} />
												{meta.label}
											</span>
										)}
									</div>
								</div>
								{isEditing && (
									<p className="prfm-status-note">
										<strong>Fail</strong>, <strong>Warning</strong>, and <strong>Missing</strong> will all display this
										finding as <strong>“Revisions Needed.”</strong>
									</p>
								)}
							</div>
							{isEditing && draft ? (
								<div className="prfm-compliance-row">
									<span className="prfm-label">Severity</span>
									<Select
										className="prfm-select"
										value={draft.severity ?? undefined}
										placeholder="None"
										allowClear
										options={PLAN_REVIEW_SEVERITY_OPTIONS.map(option => ({
											value: option.value,
											label: option.label
										}))}
										onChange={(value?: string) => findingOverride.updateDraft({ severity: value ?? null })}
									/>
								</div>
							) : (
								item.severity &&
								status !== PlanReviewFindingStatus.PASS && (
									<div className="prfm-compliance-row">
										<span className="prfm-label">Severity</span>
										<div className="prfm-compliance-value">
											{changedFields.severity && (
												<PlanReviewOllieOriginal
													fieldLabel="Severity"
													originalValue={findingSeverityLabel(ollieValues.severity)}
													className="prfm-original-link"
												/>
											)}
											<span className="prfm-value">{item.severity}</span>
										</div>
									</div>
								)
							)}
							<div className="prfm-compliance-row">
								<span className="prfm-label">Confidence</span>
								<span className="prfm-value">{confidenceLabel(item.confidence)}</span>
							</div>
							{item.requirement?.code && (
								<div className="prfm-compliance-row">
									<span className="prfm-label">Requirement</span>
									<span className="prfm-value">
										{item.requirement.code}
										{item.requirement.title ? ` — ${item.requirement.title}` : ""}
									</span>
								</div>
							)}
						</div>
					</section>

					{(correctiveAction || isEditing) && (
						<section className="prfm-section">
							<div className="prfm-action-head">
								<h3 className="prfm-action-label">
									<Wrench size={14} className="prfm-action-icon" />
									Recommended Action
								</h3>
								{changedFields.correctiveAction && (
									<PlanReviewOllieOriginal
										fieldLabel="Recommended Action"
										originalValue={ollieValues.correctiveAction}
										className="prfm-original-link"
									/>
								)}
							</div>
							<div className="prfm-action">
								{isEditing && draft ? (
									<Input.TextArea
										className="prfm-textarea"
										rows={3}
										value={draft.correctiveAction}
										onChange={event => findingOverride.updateDraft({ correctiveAction: event.target.value })}
									/>
								) : (
									<p className="prfm-action-text">{correctiveAction}</p>
								)}
							</div>
						</section>
					)}

					{(codeSections.length > 0 || hasReviewerCodeSections || isEditing) && (
						<section className="prfm-section">
							<div className="prfm-section-head">
								<h3 className="prfm-section-title">
									<Scale size={15} />
									Code sections applied
								</h3>
								{hasReviewerCodeSections && (
									<PlanReviewOllieOriginal
										fieldLabel="Code Sections"
										originalValue={ollieCodeSectionLabels}
										className="prfm-original-link"
									/>
								)}
							</div>
							{isEditing && draft ? (
								<CodeCitationPicker
									jurisdictionId={jurisdictionId ?? ""}
									value={draft.codeCitations}
									onChange={codeCitations => findingOverride.updateDraft({ codeCitations })}
								/>
							) : hasNoCodeSections ? (
								<p className="prfm-uncited">No code sections are cited for this finding.</p>
							) : (
								<>
									<p className="prfm-code-intro">
										The requirement above is derived from{" "}
										{codeSections.length === 1 ? "this section" : "these sections"} of the code. The submittal was
										reviewed against both.
									</p>
									<CodeSectionPanel
										references={codeSections}
										defaultExpanded
										hideSourceLink={!canSeeReviewerOnlyContent}
									/>
								</>
							)}
						</section>
					)}

					{/* The sections that the cited code refers to, for example "installed in accordance with
					    Section 903.3.1.1". The analysis read them with the rule, so the reader sees them too. */}
					{crossReferencedSections.length > 0 && (
						<section className="prfm-section">
							<h3 className="prfm-section-title">
								<Link2 size={15} />
								Cross-referenced code sections
							</h3>
							<p className="prfm-code-intro">
								The code sections above refer to {hasOneCrossReference ? "this section" : "these sections"} of the code
								adopted by this jurisdiction. Ollie read {hasOneCrossReference ? "it" : "them"} with the requirement.
							</p>
							<CodeSectionPanel references={crossReferencedSections} hideSourceLink={!canSeeReviewerOnlyContent} />
						</section>
					)}

					{(citationSource || item.rule_id) && (
						<section className="prfm-section">
							<h3 className="prfm-section-title">
								<BookOpen size={15} />
								Citation
							</h3>
							<div className="prfm-citation">
								<p className="prfm-citation-source">
									{citationSource ?? item.rule_id}
									{citationSection ? ` § ${citationSection}` : ""}
								</p>
								{citationPages.length > 0 && (
									<p className="prfm-citation-pages">
										{citationPages.length > 1 ? "Pages " : "Page "}
										{citationPages.map((page, index) => (
											<Fragment key={page}>
												{index > 0 && ", "}
												{pagesTargetUrl ? (
													<a
														className="prfm-citation-page"
														href={atPage(pagesTargetUrl, page)}
														target="_blank"
														rel="noreferrer"
													>
														{page}
													</a>
												) : (
													page
												)}
											</Fragment>
										))}
									</p>
								)}
								{citationUrl && (
									<a className="prfm-citation-link" href={citationUrl} target="_blank" rel="noreferrer">
										View source
										<ExternalLink size={13} />
									</a>
								)}
							</div>
						</section>
					)}

					{canGiveFeedback && (
						<PlanReviewFeedbackSection
							key={item.id}
							title={feedbackCopy.title}
							subtitle={feedbackCopy.subtitle}
							savedFeedback={latestFeedback}
							submitting={adding || updating}
							deleting={deleting}
							canChangeSavedFeedback={isOwnLatestFeedback}
							asksWhichPartWasOff={isReviewer}
							requiresReason={!isReviewer}
							incorrectSubmitLabel={canDismissFinding ? "Save Feedback and move to Dismissed" : undefined}
							incorrectNotice={canDismissFinding ? APPLICANT_DISMISS_NOTICE : undefined}
							decisionRequest={feedbackDecisionRequest}
							onSubmit={handleSubmitFeedback}
							onDelete={handleDeleteFeedback}
						/>
					)}

					{/* Hidden while Ollie's analysis is the only entry. */}
					{history.length > 1 && (
						<div className="prfm-history">
							<PlanReviewFindingHistory entries={history} />
						</div>
					)}
				</div>

				{isEditing && (
					<div className="prfm-override-footer">
						<div className="prfm-override-reason">
							<div className="prfm-override-reason-head">
								<label htmlFor={`prfm-override-reason-${item.id}`} className="prfm-override-reason-label">
									<MessageSquare size={14} className="prfm-override-reason-icon" />
									Reason / Comment for Override
								</label>
								{findingOverride.isReasonRequired ? (
									<span className="prfm-override-required">
										Required when moving to {findingStatusLabel(draft?.status)}
									</span>
								) : (
									<span className="prfm-override-optional">Optional</span>
								)}
							</div>
							<Input.TextArea
								id={`prfm-override-reason-${item.id}`}
								className="prfm-override-reason-input"
								rows={2}
								status={findingOverride.showReasonError ? "error" : undefined}
								value={findingOverride.reason}
								placeholder={
									findingOverride.isReasonRequired
										? "Explain why this finding is acceptable or not applicable so it is recorded in the audit history…"
										: "Add context or code interpretation notes for this override (recorded in change history)…"
								}
								onChange={event => findingOverride.updateReason(event.target.value)}
							/>
							{findingOverride.showReasonError && (
								<p className="prfm-override-error">
									A reason is required when moving a Fail, Warning, or Missing finding to{" "}
									{findingStatusLabel(draft?.status)}.
								</p>
							)}
						</div>
						<div className="prfm-override-actions">
							<span className="prfm-override-note">Saves one combined entry to Change history</span>
							<div className="prfm-override-buttons">
								<button type="button" className="prfm-outline-btn" onClick={findingOverride.cancelEditing}>
									Cancel
								</button>
								<button
									type="button"
									className="prfm-primary-btn"
									disabled={findingOverride.saving}
									onClick={findingOverride.saveChanges}
								>
									{findingOverride.saving ? "Saving…" : "Save changes"}
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default memo(PlanReviewFindingModal);
